import { TypedDocumentNode, gql } from '@apollo/client';
import {
  faExclamationCircle,
  faRepeat,
  faStore,
} from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import { Bold } from '@sorare/core/src/atoms/typography/Bold';
import Dialog from '@sorare/core/src/components/dialog';
import { groupBy } from '@sorare/core/src/lib/arrays';
import {
  isMyCardListedOnMarket,
  isSentInDirectOffer,
} from '@sorare/core/src/lib/cards';
import { glossary } from '@sorare/core/src/lib/glossary';
import { formatLineupDisplayName } from '@sorare/core/src/lib/so5';

import DivisionLogo from 'components/so5/DivisionLogo';

import { WarningRow } from './WarningRow';
import { WarningDialog_anyCard } from './__generated__/index.graphql';

const Body = styled(Vertical).attrs({ gap: 2 })`
  padding: var(--double-unit);
`;
const Buttons = styled(Horizontal)`
  padding: 0 var(--double-unit) var(--double-unit);
`;

type Props = {
  open: boolean;
  onCancel: () => void;
  onContinue: () => void;
  cards: WarningDialog_anyCard[];
  so5LeaderboardSlug: string;
  so5LineupId: string;
};

export const WarningDialog = ({
  open,
  onCancel,
  onContinue,
  cards,
  so5LineupId,
}: Props) => {
  const listedOnMarketCards = cards.filter(card =>
    isMyCardListedOnMarket(card)
  );
  const sentInDirectOfferCards = cards.filter(card =>
    isSentInDirectOffer(card)
  );
  const usedInOtherLineupCards = cards
    .map(anyCard => {
      const otherLineup = anyCard.concurrentSo5Lineups.find(
        l => l.id !== so5LineupId
      );

      return otherLineup ? { anyCard, otherLineup } : null;
    })
    .filter(Boolean);

  const warningCardsCount =
    listedOnMarketCards.length +
    sentInDirectOfferCards.length +
    usedInOtherLineupCards.length;

  if (!warningCardsCount) {
    return null;
  }

  return (
    <Dialog
      maxWidth="sm"
      open={open}
      title={
        <Horizontal center>
          <FontAwesomeIcon
            icon={faExclamationCircle}
            color="var(--c-yellow-400)"
          />
          <LabelL bold className="text-center">
            <FormattedMessage
              id="UsedCardsDialog.dialogTitle"
              defaultMessage="Warning"
            />
          </LabelL>
        </Horizontal>
      }
      onClose={onCancel}
      footer={
        <Buttons>
          <Button color="red" size="medium" fullWidth onClick={onCancel}>
            <FormattedMessage {...glossary.cancel} />
          </Button>
          <Button color="primary" size="medium" fullWidth onClick={onContinue}>
            <FormattedMessage {...glossary.continue} />
          </Button>
        </Buttons>
      }
    >
      <Body>
        <BodyM>
          <FormattedMessage
            id="WarningDialog.bySelectingThesePlayers"
            defaultMessage="By selecting these players, they will either be removed from their current lineup or unlisted from the market."
          />
        </BodyM>
        <Vertical gap={2}>
          {usedInOtherLineupCards.map(({ anyCard, otherLineup }) => (
            <WarningRow
              key={anyCard.slug}
              card={anyCard}
              warning={
                <>
                  {otherLineup.so5Leaderboard && (
                    <DivisionLogo
                      size={3}
                      so5Leaderboard={otherLineup.so5Leaderboard}
                    />
                  )}
                  <LabelM>
                    <FormattedMessage
                      id="WarningDialog.alreadyUsedInLineup"
                      defaultMessage="Already used in <b>{lineupDisplayName}</b>"
                      values={{
                        lineupDisplayName: formatLineupDisplayName(otherLineup),
                        b: Bold,
                      }}
                    />
                  </LabelM>
                </>
              }
            />
          ))}
          {Object.values(
            groupBy(
              [...listedOnMarketCards, ...sentInDirectOfferCards],
              card => card.slug
            )
          )
            .filter(Boolean)
            .map(([anyCard]) => (
              <WarningRow
                key={anyCard.slug}
                card={anyCard}
                warningLevel="error"
                warning={
                  <Vertical>
                    {listedOnMarketCards.some(c => c.slug === anyCard.slug) && (
                      <Horizontal>
                        <FontAwesomeIcon
                          icon={faStore}
                          width={12}
                          height={12}
                        />
                        <LabelM>
                          <FormattedMessage
                            id="WarningDialog.listedOnTheMarket"
                            defaultMessage="Listed on the Market"
                            values={{
                              b: Bold,
                            }}
                          />
                        </LabelM>
                      </Horizontal>
                    )}
                    {sentInDirectOfferCards.some(
                      c => c.slug === anyCard.slug
                    ) && (
                      <Horizontal>
                        <FontAwesomeIcon
                          icon={faRepeat}
                          width={12}
                          height={12}
                        />
                        <LabelM>
                          <FormattedMessage
                            id="WarningDialog.ListedInTradeOffers"
                            defaultMessage="Listed in trade offer(s)"
                            values={{
                              b: Bold,
                            }}
                          />
                        </LabelM>
                      </Horizontal>
                    )}
                  </Vertical>
                }
              />
            ))}
        </Vertical>
      </Body>
    </Dialog>
  );
};

WarningDialog.fragments = {
  anyCard: gql`
    fragment WarningDialog_anyCard on AnyCardInterface {
      slug
      concurrentSo5Lineups(so5LeaderboardSlug: $so5LeaderboardSlug) {
        id
        so5Fixture {
          slug
        }
        so5Leaderboard {
          slug
          ...DivisionLogo_so5Leaderboard
        }
        ...formatLineupDisplayName_so5Lineup
      }
      sentInLiveOffers {
        id
        type
        endDate
      }
      ...WarningRow_anyCard
      ...isMyCardListedOnMarket_anyCard
      ...isSentInDirectOffer_anyCard
    }
    ${WarningRow.fragments.anyCard}
    ${isMyCardListedOnMarket.fragments.anyCard}
    ${isSentInDirectOffer.fragments.anyCard}
    ${formatLineupDisplayName.fragments.so5Lineup}
    ${DivisionLogo.fragments.so5Leaderboard}
  ` as TypedDocumentNode<WarningDialog_anyCard>,
};
